/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ListRowPropsExtended } from '@cambridgeassessment/cambridge-ui/build/components/InfiniteScroll/InfiniteScroll';
import {
  Button,
  Headline,
  InfiniteScroll,
  Paragraph,
  Title,
  Tooltip,
  Tabs,
  Tab,
} from '@cambridgeassessment/cambridge-ui';
import AddIcon from '@material-ui/icons/Add';
import thunks from '../../../store/consortiaManagement/thunks';
import { RootState } from '../../../store';
import Loader from '../../../components/Loader/Loader';
import {
  StyledConsortiumItemContainer,
  StyledContainer,
  StyledHeaderContainer,
  StyledParagraph,
  StyledSearchFieldContainer,
  StyledDeleteModal,
  StyledGridItem,
  StyledWarningModal,
  StyledFlexContainer,
} from './ConsortiaManagement.styles';
import actions from '../../../store/consortiaManagement/actions';
import ConsortiumCard from './components/ConsortiumCard';
import SearchField from './components/SearchField';
import { BUSINESS_UNITS, Roles } from '@cambridgeassessment/lib-ee-commons';
import { StyledTextContainer } from './components/ConsortiumCard.styles';
import { Authorized } from 'lib-browser';

const ConsortiaManagementPage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [openedCards, setOpenedCards] = useState<number[]>([0]);
  const {
    userData: { businessUnit, selectedRole },
    consortiaManagement: { isLoading, fetchingConsortia, hasError, consortia, consortiaTotal, searchValue, filter },
  } = useSelector((state: RootState) => state);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [warningDialogOpen, setWarningDialogOpen] = useState<boolean>(false);
  const [editConsortium, setEditConsortium] = useState<string>('');
  const [consortiumToDelete, setConsortiumToDelete] = useState<{ id: string; name: string } | null>(null);
  const [tabIndex, setTabIndex] = useState(0);

  const navigateToCreateForm = useCallback((): void => history.push('/manageConsortia/createNewConsortium'), [history]);
  const navigateToHomePage = useCallback((): void => history.push('/'), [history]);

  useEffect(() => {
    if (businessUnit?.id !== BUSINESS_UNITS.OCR.id) navigateToHomePage();
    return () => {
      dispatch(actions.setFilterValue('current'));
    };
  }, [businessUnit]);

  const fetchConsortia = useCallback(
    (reset?: boolean, setFetchState?: boolean, resolver?: () => void) => {
      dispatch(thunks.fetchConsortia(reset, setFetchState, resolver));
    },
    [dispatch],
  );
  useEffect(() => {
    fetchConsortia(true);
  }, [fetchConsortia]);

  useEffect(() => {
    fetchConsortia(true);
    setTabIndex(filter?.series === 'current' ? 0 : 1);
    setOpenedCards([0]);
  }, [filter?.series]);

  const loadMoreItems = (): Promise<void> => {
    return new Promise((resolve) => {
      fetchConsortia(false, true, resolve);
    });
  };
  const setSearchValue = (value: string): ReturnType<typeof dispatch> => dispatch(actions.setSearchValue(value));

  const initConsortiumDeletion = (id: string, name: string): void => {
    setDeleteDialogOpen(true);
    setConsortiumToDelete({ id, name });
  };

  const onDeleteConsortium = (): void => {
    if (consortiumToDelete?.id) {
      dispatch(thunks.deleteConsortium(consortiumToDelete.id));
      setDeleteDialogOpen(false);
    }
  };

  const initEditConsortium = (id: string): void => {
    setWarningDialogOpen(true);
    setEditConsortium(id);
  };

  const onEditConsortium = (): void => {
    history.push(`/manageConsortia/editConsortium/${editConsortium}`);
  };

  const setCardOpened = (index: number): void => setOpenedCards([...openedCards, index]);

  const setCardClosed = (index: number): void => setOpenedCards(openedCards.filter((item) => item !== index));

  const handleChange = (_event: unknown, tabIndexValue: number): void => {
    dispatch(actions.setFilterValue(tabIndexValue === 0 ? 'current' : 'past'));
  };

  const renderRow = ({ key, index, style, measure, cache }: ListRowPropsExtended): JSX.Element | undefined => {
    if (!consortia?.length) return;
    const consortium = consortia[index];

    const recalculateSize = (): void => {
      if (measure) measure();
      cache.clear(index, 0);
    };
    const calcMinHeight = 210 + 10 * consortium.centres.length;

    const TransitionProps = {
      onExited: (): void => recalculateSize(),
      onEntered: (): void => recalculateSize(),
      onEntering: (): void => recalculateSize(),
      timeout: 400,
    };

    const isCardCollapsed = openedCards.find((card) => card === index) === undefined;
    const onClickHandler = isCardCollapsed ? setCardOpened : setCardClosed;

    return (
      <StyledConsortiumItemContainer
        onClick={() => onClickHandler(index)}
        minHeight={calcMinHeight}
        style={style}
        key={key}
        className={isCardCollapsed ? 'collapsed' : 'expanded'}
        data-qa-id={`consortium-card-container-${index}`}
      >
        <ConsortiumCard
          data-qa-id='consortium-card'
          consortium={consortium}
          isExpanded={!isCardCollapsed}
          index={index}
          transitionProps={TransitionProps}
          onDelete={initConsortiumDeletion}
          onEditConsortium={initEditConsortium}
        />
      </StyledConsortiumItemContainer>
    );
  };

  if (isLoading) return <Loader />;

  if (hasError) return <div>Failed to get consortia data</div>;

  return (
    <Authorized selectedRole={selectedRole} roles={[Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR]}>
      <StyledContainer data-qa-id='page-priming-consortia-management'>
        <StyledHeaderContainer>
          <Headline>Manage consortia</Headline>
          <StyledGridItem>
            <Button
              onClick={navigateToCreateForm}
              qaId='add-consortium-button'
              startIcon={<AddIcon />}
              variant='outlined'
            >
              Create new
            </Button>
          </StyledGridItem>
          <StyledGridItem>
            <StyledParagraph>
              Create, edit and delete consortia. Click each consortium to open it&apos;s details. The first one is open
              for you.
            </StyledParagraph>
            <Title data-qa-id='consortia-header'>Consortia</Title>
            <StyledFlexContainer>
              <StyledTextContainer>View:</StyledTextContainer>
              <Tabs value={tabIndex} onChange={handleChange}>
                <Tab qaId='current-series-tab' label='Current series' disabled={fetchingConsortia} />
                <Tab qaId='past-series-tab' label='Past series' disabled={fetchingConsortia} />
              </Tabs>
            </StyledFlexContainer>
          </StyledGridItem>
          <StyledSearchFieldContainer>
            <Tooltip
              arrow={true}
              placement='top-start'
              dataQaId='consortium-search-tooltip'
              title='Search by Consortia name, Centre Number, Centre Name, Series, or Syllabus/Subject and Component number'
              enterDelay={500}
            >
              <span>
                <SearchField fetchConsortia={fetchConsortia} setValue={setSearchValue} value={searchValue} />
              </span>
            </Tooltip>
          </StyledSearchFieldContainer>
        </StyledHeaderContainer>
        <StyledContainer data-qa-id='page-priming-consortia'>
          {consortia?.length === 0 && !searchValue && (
            <Paragraph>
              {filter.series === 'current' && 'There are no consortia created for the current series.'}
              {filter.series === 'past' && 'There are no consortia created for the past series.'}
            </Paragraph>
          )}
          {consortia?.length === 0 && searchValue && (
            <>
              <Headline>No results found for ‘{searchValue}’</Headline>{' '}
              <Paragraph>Try searching with another keyword, number or check your spelling.</Paragraph>
            </>
          )}
          <InfiniteScroll
            rowRenderer={renderRow}
            loadMoreItems={loadMoreItems}
            total={consortiaTotal}
            data={consortia || []}
            threshold={5}
          />
          <StyledDeleteModal
            open={deleteDialogOpen}
            title='Delete consortium'
            body=''
            btnLeftText='Cancel'
            btnRightText='Delete'
            dataQaId='delete-consortium-modal'
            onClickBtnRight={onDeleteConsortium}
            onClickBtnLeft={() => setDeleteDialogOpen(false)}
          >
            Are you sure you want to delete the consortium <b>{consortiumToDelete?.name}</b>, as the sample algorithm
            may have already run?
          </StyledDeleteModal>
          <StyledWarningModal
            open={warningDialogOpen}
            title='The sampling algorithm for this consortium has already been run for this series'
            body='Are you sure you want to continue?'
            btnLeftText='No'
            btnRightText='Yes'
            dataQaId='warning-consortium-modal'
            onClickBtnRight={onEditConsortium}
            onClickBtnLeft={() => setWarningDialogOpen(false)}
          />
        </StyledContainer>
      </StyledContainer>
    </Authorized>
  );
};

export default ConsortiaManagementPage;
